import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveImage } from '@blaze-cms/image-cdn-react';
import { FaTimes } from 'react-icons/fa';
import BlazeLink from '../../BlazeLink';
import ImageDetails from '../ImageDetails';
import NavButton from './NavButton';
import getImageData from '../../../utils/get-image-data';
import useGetImages from '../../../hooks/use-get-images';
import { shouldShowProperty } from '../../../helpers';

const GlobalLightbox = ({ images, onClose, selectedImage, metaTitle, parentName }) => {
  const [index, setIndex] = useState(selectedImage);

  const currentImage = images[index] || {};

  const {
    imageId,
    url,
    name,
    enableLightbox,
    isCaptionDisplayed,
    areImageCreditsDisplayed,
    imageCredits,
    credits,
    ...otherProps
  } = currentImage;

  const { data, loading, error } = useGetImages(imageId);

  if (error) {
    return error.message;
  }

  const { url: imageUrl = '', data: imageData = {} } = data.getFile || {
    getFile: { url: '', data: {} }
  };

  const urlProps = { href: url, title: name };
  const imgWrapperProps = url ? urlProps : {};

  const { caption, altText } = getImageData(otherProps, imageData);

  const shouldDisplayCaption = shouldShowProperty({
    isDisplayed: isCaptionDisplayed,
    value: caption
  });

  // depreciated: imageCredits is depreciated. This code is to handle legacy data. check before changing/removing
  const shouldDisplayCredits = shouldShowProperty({
    isDisplayed: areImageCreditsDisplayed,
    value: credits || imageCredits
  });

  const navigate = position => {
    const newPosition = index + position;
    if (images[newPosition]) {
      setIndex(newPosition);
    }
  };

  const handlePrevious = () => navigate(-1);
  const handleNext = () => navigate(1);
  const handleClose = () => onClose();

  const showNext = index !== images.length - 1;
  const showPrevious = !!index;

  const imageCounter = `${index + 1} of ${images.length}`;

  return (
    <>
      {!!images.length && (
        <div>
          <div className="overlay" />
          <div className="modal modal--full-screen modal--gallery modal--show">
            <header className="modal__header modal__header--gallery">
              <div className="modal__title modal__title--gallery">
                {parentName || metaTitle || name}
              </div>
              <div
                role="button"
                className="modal__close"
                onClick={handleClose}
                aria-label="Close modal">
                <i>
                  <FaTimes />
                </i>
              </div>
            </header>
            <div className="modal__content modal__content--gallery">
              <div className="gallery">
                <div className="gallery__column gallery__column--slider">
                  <div className="gallery__slider">
                    <div className="slider__wrapper slider__wrapper--gallery open">
                      <div className="slider__header slider__header--gallery">
                        <div
                          className="slider__counter slider__counter--gallery"
                          data-testid="image-counter">
                          {imageCounter}
                        </div>
                      </div>

                      <NavButton
                        handleAction={handlePrevious}
                        show={showPrevious}
                        modifier="previous"
                        aria-label="Previous Image"
                      />

                      <div className="slider slider--gallery">
                        <div className="slider__list slider__list--gallery">
                          <div className="slider__list-wrapper slider__list-wrapper--gallery">
                            <div className="slider__slide slider__slide--gallery">
                              {loading && <div />}
                              {Object.keys(imgWrapperProps).length ? (
                                <BlazeLink {...imgWrapperProps}>
                                  <ResponsiveImage
                                    src={imageUrl}
                                    alt={altText}
                                    sizeKey="globalLightbox"
                                    data-testid="globalLightboxImage"
                                  />
                                </BlazeLink>
                              ) : (
                                <Fragment>
                                  <ResponsiveImage
                                    src={imageUrl}
                                    alt={altText}
                                    sizeKey="globalLightbox"
                                    data-testid="globalLightboxImage"
                                  />
                                </Fragment>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <NavButton
                        aria-label="Next Image"
                        handleAction={handleNext}
                        show={showNext}
                        modifier="next"
                      />
                    </div>
                    {(shouldDisplayCaption || shouldDisplayCredits) && (
                      <ImageDetails
                        caption={caption}
                        credits={credits || imageCredits}
                        shouldDisplayCaption={shouldDisplayCaption}
                        shouldDisplayCredits={shouldDisplayCredits}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

GlobalLightbox.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      imageId: PropTypes.string
    })
  ),
  metaTitle: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  selectedImage: PropTypes.number,
  parentName: PropTypes.string
};

GlobalLightbox.defaultProps = {
  metaTitle: '',
  images: [],
  selectedImage: 0,
  parentName: ''
};

export default GlobalLightbox;
